import { Editor } from '@tiptap/core'
import { BubbleMenu, BubbleMenuProps } from '@tiptap/react'
import { useEffect, useRef, useState } from 'react'
import { useClickAway } from 'react-use'

import styles from './LinkMenu.module.scss'

import useImportFromConfluence, { confluenceLink } from '~hooks/confluence/useImportFromConfluence'
import { useTimeout } from '~hooks/useTimeout'
import DownloadIcon from '~icons/download.svg'
import EditIcon from '~icons/edit.svg'
import ExternalIcon from '~icons/external.svg'
import GoogleIcon from '~icons/illustrations/google.svg'
import TrashIcon from '~icons/trash.svg'
import getMarkRange from '~lib/editor/helpers/getMarkRange'
import useImportFromGoogle, { goolgleDocsLink } from '~lib/hooks/google/useImportFromGoogle'
import MenuLayout, { MenuVariant } from '~lib/ui-kit/MenuLayout'
import MenuList from '~lib/ui-kit/MenuList'

interface LinkMenuProps {
  editor: Editor
  googleDocsEnabled?: boolean
  confluenceEnabled?: boolean
  opened: boolean
  onClose: () => void
  onShowLinkEditor: () => void
}

const TIPPY_OPTIONS: BubbleMenuProps['tippyOptions'] = {
  placement: 'bottom',
}

function shouldShowBubbleMenu() {
  return true
}

function LinkMenu({ editor, confluenceEnabled, googleDocsEnabled, opened, onClose, onShowLinkEditor }: LinkMenuProps) {
  const [includesGoogleDocsLink, setIncludesGoogleDocsLink] = useState(false)
  const [includesConfluenceLink, setIncludesConfluenceLink] = useState(false)
  const menuRef = useRef<HTMLUListElement>(null)
  const { importFromGoogle } = useImportFromGoogle()
  const { importFromConfluence } = useImportFromConfluence()
  const { setTimeout } = useTimeout()

  useEffect(() => {
    if (!opened) {
      return
    }

    setTimeout(() => {
      let { href = '' } = editor.getAttributes('link')
      let inclGDocsLink = false
      let inclConfLink = false

      if (!href) {
        const { nodeBefore, nodeAfter } = editor.state.selection.$to

        nodeBefore?.marks.forEach((m) => {
          if (typeof m.attrs.href === 'string') {
            href = m.attrs.href
          }
        })

        nodeAfter?.marks.forEach((m) => {
          if (typeof m.attrs.href === 'string') {
            href = m.attrs.href
          }
        })
      }

      if (googleDocsEnabled && href.includes(goolgleDocsLink)) {
        inclGDocsLink = true
      }

      if (confluenceEnabled && href.includes(confluenceLink)) {
        inclConfLink = true
      }

      setIncludesGoogleDocsLink(inclGDocsLink)
      setIncludesConfluenceLink(inclConfLink)
    }, 100)
  }, [confluenceEnabled, editor, googleDocsEnabled, opened, setTimeout])

  useClickAway(menuRef, () => {
    onClose()
  })

  function restoreSelection() {
    if (editor.isActive('link')) {
      editor.commands.focus()
    } else {
      const pos = editor.state.selection.to - 1
      const range = getMarkRange(editor, pos, editor.schema.marks.link)
      editor.commands.setTextSelection(range)
    }
  }

  function replaceContent(html: string) {
    const range = getMarkRange(editor, editor.state.selection.from, editor.schema.marks.link)

    editor
      .chain()
      .setTextSelection(range)
      .deleteSelection()
      .insertContent(html)
      .setTextSelection(range.from + html.length)
      .run()
  }

  const handleEditClick = () => {
    restoreSelection()
    onShowLinkEditor()
    onClose()
  }

  async function handleImportFromGoogle() {
    restoreSelection()
    const { href = '' } = editor.getAttributes('link')
    const html = await importFromGoogle(href)
    replaceContent(html)
    onClose()
  }

  async function handleImportFromConfluence() {
    restoreSelection()
    const { href = '' } = editor.getAttributes('link')
    const html = await importFromConfluence(href)
    replaceContent(html)
    onClose()
  }

  function handleOpenClick() {
    restoreSelection()
    const { href = '' } = editor.getAttributes('link')
    window.open(href, '_blank')
    onClose()
  }

  function handleRemoveClick() {
    restoreSelection()
    editor.commands.unsetLink()
    onClose()

    requestAnimationFrame(() => {
      editor.commands.focus()
    })
  }

  return (
    <BubbleMenu
      className={styles.root}
      shouldShow={shouldShowBubbleMenu}
      tippyOptions={TIPPY_OPTIONS}
      pluginKey="link-menu"
      editor={editor}
    >
      {opened && (
        <MenuLayout variant={MenuVariant.CONTEXT}>
          <MenuList ref={menuRef}>
            {includesGoogleDocsLink && (
              <MenuList.Item icon={<GoogleIcon />} onClick={handleImportFromGoogle}>
                Import from Google
              </MenuList.Item>
            )}

            {includesConfluenceLink && (
              <MenuList.Item icon={<DownloadIcon />} onClick={handleImportFromConfluence}>
                Import from Confluence
              </MenuList.Item>
            )}

            <MenuList.Item icon={<EditIcon />} onClick={handleEditClick}>
              Edit
            </MenuList.Item>

            <MenuList.Item icon={<ExternalIcon />} onClick={handleOpenClick}>
              Open in new tab
            </MenuList.Item>

            <MenuList.Item icon={<TrashIcon />} onMouseDown={handleRemoveClick}>
              Remove
            </MenuList.Item>
          </MenuList>
        </MenuLayout>
      )}
    </BubbleMenu>
  )
}

export default LinkMenu
